import React, { useState } from 'react';
import { updateFactoryProduct, uploadFactoryProductImage } from '../../lib/catalogImport';

// Shared in-place quick editor for a factory's catalog (factory_products), used by
// both DashboardFactory (the owner dashboard) and the "prepared for you" section of
// DashboardSupplier's My Products tab. Edits price/currency/moq and replaces the
// image directly on factory_products (owner RLS). Any owner save stamps
// owner_confirmed_at → the product shows a "Confirmed" tag (vs "Awaiting price").
// `renderActions(p)` lets the host add extra per-card buttons (full edit / delete).

const nf = (v) => (v && v !== 'not_found' ? String(v).trim() : '');
const FB = "'Tajawal', sans-serif";

const STR = {
  ar: { confirmed: '✓ مؤكّد', awaiting: 'بانتظار السعر', quickEdit: 'تعديل سريع', price: 'السعر', currency: 'العملة', moq: 'MOQ', save: 'حفظ', saving: 'جارٍ…', cancel: 'إلغاء', replaceImg: 'استبدال الصورة', noPrice: 'بلا سعر' },
  en: { confirmed: '✓ Confirmed', awaiting: 'Awaiting price', quickEdit: 'Quick edit', price: 'Price', currency: 'Currency', moq: 'MOQ', save: 'Save', saving: '…', cancel: 'Cancel', replaceImg: 'Replace image', noPrice: 'No price' },
  zh: { confirmed: '✓ 已确认', awaiting: '待定价', quickEdit: '快速编辑', price: '价格', currency: '币种', moq: 'MOQ', save: '保存', saving: '…', cancel: '取消', replaceImg: '更换图片', noPrice: '暂无价格' },
};

export default function FactoryCatalogQuickEdit({ products, setProducts, lang = 'ar', renderActions }) {
  const isAr = lang === 'ar';
  const t = STR[lang] || STR.ar;
  const [quickId, setQuickId] = useState(null);
  const [qv, setQv] = useState({ price: '', currency: '', moq: '' });
  const [busy, setBusy] = useState(false);

  const priceLabel = (p) => { const v = nf(p.price); return v ? `${v}${nf(p.currency) ? ` ${nf(p.currency)}` : ''}` : t.noPrice; };
  const pName = (p) => (isAr ? (nf(p.name_ar) || nf(p.name_en)) : lang === 'zh' ? (nf(p.name_zh) || nf(p.name_en)) : (nf(p.name_en) || nf(p.name_ar))) || nf(p.name_ar) || '—';

  async function saveQuick(p) {
    setBusy(true);
    try {
      const patch = { price: nf(qv.price) || null, currency: nf(qv.currency) || null, moq: nf(qv.moq) || null, owner_confirmed_at: new Date().toISOString() };
      await updateFactoryProduct(p.id, patch);
      setProducts((ps) => ps.map((x) => (x.id === p.id ? { ...x, ...patch } : x)));
      setQuickId(null);
    } catch (e) { alert(e.message || 'Save failed'); }
    setBusy(false);
  }
  async function replaceImage(p, file) {
    if (!file) return;
    setBusy(true);
    try {
      const url = await uploadFactoryProductImage(file);
      const patch = { image: url, owner_confirmed_at: new Date().toISOString() };
      await updateFactoryProduct(p.id, patch);
      setProducts((ps) => ps.map((x) => (x.id === p.id ? { ...x, ...patch } : x)));
    } catch (e) { alert(e.message || 'Upload failed'); }
    setBusy(false);
  }

  return (
    <>
      <style>{CSS}</style>
      <div className="fqe-grid">
        {products.map((p) => (
          <div className="fqe-card" key={p.id}>
            <div className="fqe-img">
              {p.image && <img src={p.image} alt="" loading="lazy" />}
              <label className="fqe-up" title={t.replaceImg}>⤒
                <input type="file" accept="image/*" style={{ display: 'none' }}
                  onChange={(e) => { replaceImage(p, e.target.files?.[0]); e.target.value = ''; }} />
              </label>
              <span className={`fqe-tag ${p.owner_confirmed_at ? 'ok' : 'wait'}`}>{p.owner_confirmed_at ? t.confirmed : t.awaiting}</span>
            </div>
            <div className="fqe-body">
              <p className="fqe-name">{pName(p)}</p>
              {quickId === p.id ? (
                <div>
                  <div className="fqe-qerow">
                    <input className="fqe-in" placeholder={t.price} dir="ltr" value={qv.price} onChange={(e) => setQv((v) => ({ ...v, price: e.target.value }))} />
                    <input className="fqe-in cur" placeholder={t.currency} dir="ltr" value={qv.currency} onChange={(e) => setQv((v) => ({ ...v, currency: e.target.value }))} />
                  </div>
                  <input className="fqe-in" placeholder={t.moq} dir="ltr" value={qv.moq} onChange={(e) => setQv((v) => ({ ...v, moq: e.target.value }))} />
                  <div className="fqe-act" style={{ marginTop: 6 }}>
                    <button className="fqe-btn" disabled={busy} onClick={() => saveQuick(p)}>{busy ? t.saving : t.save}</button>
                    <button className="fqe-btn" onClick={() => setQuickId(null)}>{t.cancel}</button>
                  </div>
                </div>
              ) : (
                <>
                  <p className={`fqe-price ${nf(p.price) ? 'on' : 'off'}`}>{priceLabel(p)}</p>
                  <div className="fqe-act">
                    <button className="fqe-btn" onClick={() => { setQuickId(p.id); setQv({ price: nf(p.price), currency: nf(p.currency), moq: nf(p.moq) }); }}>{t.quickEdit}</button>
                    {renderActions ? renderActions(p) : null}
                  </div>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

const CSS = `
.fqe-grid { display:grid; grid-template-columns:repeat(auto-fill, minmax(150px, 1fr)); gap:12px; }
.fqe-card { border:1px solid rgba(0,0,0,0.09); border-radius:10px; overflow:hidden; background:#fff; }
.fqe-img { aspect-ratio:1/1; background:#f0ebe1; position:relative; }
.fqe-img img { width:100%; height:100%; object-fit:cover; display:block; }
.fqe-up { position:absolute; top:6px; inset-inline-end:6px; width:26px; height:26px; border-radius:7px; background:rgba(26,24,20,0.72); color:#fff; display:flex; align-items:center; justify-content:center; font-size:14px; cursor:pointer; }
.fqe-tag { position:absolute; top:6px; inset-inline-start:6px; font-size:10px; font-weight:700; padding:2px 8px; border-radius:99px; }
.fqe-tag.ok { background:rgba(45,106,79,.92); color:#fff; }
.fqe-tag.wait { background:rgba(166,134,79,.92); color:#fff; }
.fqe-body { padding:9px 10px; font-family:${FB}; }
.fqe-name { font-size:12px; font-weight:500; color:rgba(0,0,0,0.82); margin:0 0 7px; line-height:1.4; min-height:2.6em; overflow:hidden; }
.fqe-price { font-size:12.5px; font-weight:700; margin:0 0 7px; }
.fqe-price.on { color:#1A1814; }
.fqe-price.off { color:#A6864F; font-weight:600; font-size:12px; }
.fqe-qerow { display:flex; gap:5px; margin-bottom:5px; }
.fqe-in { width:100%; padding:5px 7px; border:1px solid rgba(0,0,0,0.14); border-radius:6px; font-size:11.5px; font-family:${FB}; box-sizing:border-box; }
.fqe-in.cur { max-width:64px; }
.fqe-act { display:flex; gap:6px; flex-wrap:wrap; }
.fqe-btn { flex:1 1 auto; min-width:0; font-size:11.5px; padding:5px; border-radius:6px; cursor:pointer; font-family:${FB}; border:1px solid rgba(0,0,0,0.14); background:#fff; color:#1A1814; }
.fqe-btn.del { border-color:#c0503f; color:#c0503f; }
`;
